/**
 * Free Tier Dashboard API Service
 * Functions for fetching free tier user data
 */

import {
  FreeTierDashboardData,
  UserDetailResponse,
  SimulationFullContent,
  ChatSessionFull,
  TimeRange,
} from './types';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ApiResponse<T> {
  data: T | null;
  error: string | null;
}

async function fetchWithAuth<T>(url: string): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: errorData.detail || `HTTP ${response.status}: Failed to fetch`,
      };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('API Error:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An error occurred',
    };
  }
}

export async function fetchFreeTierDashboard(
  timeRange: TimeRange
): Promise<ApiResponse<FreeTierDashboardData>> {
  return fetchWithAuth<FreeTierDashboardData>(
    `${API_URL}/admin/free-tier/dashboard?time_range=${timeRange}`
  );
}

export async function fetchFreeTierUserDetail(
  userId: number,
  timeRange: TimeRange
): Promise<ApiResponse<UserDetailResponse>> {
  return fetchWithAuth<UserDetailResponse>(
    `${API_URL}/admin/free-tier/users/${userId}?time_range=${timeRange}`
  );
}

export async function fetchSimulationContent(
  simulationId: number
): Promise<ApiResponse<SimulationFullContent>> {
  return fetchWithAuth<SimulationFullContent>(
    `${API_URL}/admin/free-tier/simulations/${simulationId}`
  );
}

export async function fetchChatConversation(
  sessionId: number
): Promise<ApiResponse<ChatSessionFull>> {
  return fetchWithAuth<ChatSessionFull>(
    `${API_URL}/admin/free-tier/chats/${sessionId}`
  );
}

export async function fetchFreeTierFeatureRuns(
  timeRange: TimeRange,
  userId?: number
): Promise<ApiResponse<{ feature_runs: any[] }>> {
  const userParam = userId ? `&user_id=${userId}` : '';
  return fetchWithAuth(
    `${API_URL}/admin/free-tier/feature-runs?time_range=${timeRange}${userParam}`
  );
}

export async function fetchFreeTierUsecaseBreakdown(
  timeRange: TimeRange,
  userId?: number
): Promise<ApiResponse<{ usecase_breakdown: any[] }>> {
  const userParam = userId ? `&user_id=${userId}` : '';
  return fetchWithAuth(
    `${API_URL}/admin/free-tier/usecase-breakdown?time_range=${timeRange}${userParam}`
  );
}

export async function fetchFreeTierUsers(
  timeRange: TimeRange
): Promise<ApiResponse<{ users: any[] }>> {
  return fetchWithAuth(
    `${API_URL}/admin/free-tier/users?time_range=${timeRange}`
  );
}
